import * as Sentry from "@sentry/react";

const isDevelopment = import.meta.env.VITE_APP_ENV === "development";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

export const initErrorTracking = () => {
  if (!sentryDsn || isDevelopment) {
    console.log("Sentry disabled for development environment");
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_APP_ENV,
    release: import.meta.env.VITE_APP_VERSION,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      // Filter out non-critical errors in production
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes("Non-Error promise rejection captured")) {
          return null;
        }
        if (error?.value?.includes("Script error")) {
          return null;
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Filter out console logs in production
      if (breadcrumb.category === "console" && breadcrumb.level !== "error") {
        return null;
      }
      return breadcrumb;
    },
  });
};

export const captureError = (error: Error, context?: Record<string, any>) => {
  if (isDevelopment) {
    console.error("Error captured:", error, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }
    Sentry.captureException(error);
  });
};

export const captureMessage = (
  message: string,
  level: "info" | "warning" | "error" = "info"
) => {
  if (isDevelopment) {
    console.log(`[${level.toUpperCase()}] ${message}`);
    return;
  }

  Sentry.captureMessage(message, level);
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  name?: string;
}) => {
  Sentry.setUser(user);
};

export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

export default {
  initErrorTracking,
  captureError,
  captureMessage,
  setUserContext,
  addBreadcrumb,
};
